/*
 * A copy of the dispatch console's module of the same name, kept byte-for-byte except for the
 * storage keys and the prose that named the other console.
 *
 * Nothing in it is design content: it is the OpenID Connect authorization-code flow with PKCE, the
 * token refresh, the expiry and the claim reading, all of which both consoles need identically.
 * It is a copy rather than a shared package because this repository builds every package in
 * `packages/` through three explicit steps in the root `package.json`, and a sixth package that
 * `npm run check` could not build before its consumers would be worse than this duplication.
 * `docs/adr/0021-two-consoles-and-no-shared-shell.md` records the decision, what it costs, and the
 * precondition for undoing it.
 */
import { useCallback, useEffect, useSyncExternalStore } from "react";

import type { AuthenticationState, ConsoleAuthority } from "./authority.ts";

/**
 * The shortest a renewal timer is allowed to be.
 *
 * Without a floor this is a tight loop. A renewal that fails because the provider is briefly
 * unreachable leaves the token exactly as aged as it was, so the next delay would be zero, and the
 * console would hammer the token endpoint for as long as the outage lasted. Five seconds is long
 * enough to be a retry and short enough that an agent does not notice the recovery.
 */
const MINIMUM_RENEWAL_DELAY_MS = 5_000;

/**
 * Subscribe a component to who is signed in, and keep the token ahead of its expiry.
 *
 * Two things, because they are one thing: the component tree and the renewal timer have to agree
 * about the session, and the way to guarantee that is for the timer to live where the state is read.
 *
 * The renewal is belt as well as braces. `ConsoleAuthority.accessToken()` already refreshes a token
 * that is about to expire before a request is allowed to carry it, so nothing depends on this timer
 * firing. What it adds is the idle case: a console left open on a board that is not polling would
 * otherwise let its refresh token sit until the provider's idle timeout had passed, and the agent
 * would be signed out by having done nothing.
 */
export function useAuthentication(authority: ConsoleAuthority): AuthenticationState {
  const subscribe = useCallback(
    (listener: () => void) => authority.subscribe(listener),
    [authority],
  );
  const state = useSyncExternalStore(
    subscribe,
    () => authority.getState(),
    () => authority.getState(),
  );

  useEffect(() => {
    if (state.kind !== "signed_in") return;
    const until = authority.millisecondsUntilRenewal();
    if (until === undefined) return;
    const timer = setTimeout(
      () => {
        void authority.renew();
      },
      Math.max(MINIMUM_RENEWAL_DELAY_MS, until),
    );
    return (): void => {
      clearTimeout(timer);
    };
    // `state` rather than `state.kind`: every renewal publishes a new session, which is what
    // re-arms this timer for the next one.
  }, [authority, state]);

  return state;
}
